<template>
  <div class="photo-picker">
    <!-- 顶部操作栏 -->
    <div class="action-bar">
      <!-- 上传控件 -->
      <van-uploader
          v-model="fileList"
          :before-upload="handleBeforeUpload"
          :after-read="handleUpload"
          :max-count="maxCount"
          :max-size="maxSize"
          :preview-image="false"
          :multiple="multiple"
          accept="image/*"
          @oversize="onOversize"
      >
      </van-uploader>
      <van-button type="danger" v-if="showPreview" round size="small" @click="handleClearAll">一键清除</van-button>
    </div>

    <!-- 图片预览网格 -->
    <div v-if="showPreview" class="preview-grid">
      <div
          v-for="(item, index) in previewList"
          :key="index"
          class="preview-item"
          @touchstart="handleTouchStart(index)"
          @touchend="handleTouchEnd"
          @click="handleSelect(index)"
      >
        <van-image
            fit="cover"
            :src="item.content || item.resUrl"
            :class="{ 'selected': item.selected }"
        />
        <!-- 选中标记 -->
        <div v-show="item.selected" class="checkmark">
          <van-icon name="success" size="20" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {Uploader, Button, Image, Icon, Toast,Dialog} from 'vant';
import {mapGetters} from "vuex";
import compressor from 'compressorjs';
export default {
  components: {
    [Uploader.name]: Uploader,
    [Button.name]: Button,
    [Image.name]: Image,
    [Icon.name]: Icon
  },

  props: {
    // 最大上传数量
    maxCount: {
      type: Number,
      default: 12
    },
    // 是否支持多选
    multiple: {
      type: Boolean,
      default: true
    },
    // 最大文件大小（单位：KB）
    maxSize: {
      type: Number,
      default: 50 * 1024 * 1024 // 5MB
    },
    // 是否显示删除按钮
    showDelete: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      longPressTimer: null,
      currentIndex: -1,
      page: 1,
      size: 24,
      fileList: [],      // 上传组件绑定的文件列表
      previewList: []    // 预览的图片列表
    };
  },
  computed: {
    showPreview() {
      return this.previewList.length > 0;
    },
    ...mapGetters(["userInfo"]),
  },
  created() {
    this.loadPicList();
  },
  methods: {
    // 修改后的handleBeforeUpload方法
    async handleBeforeUpload(file) {
      // 增强格式验证
      const isImage = ['image/jpeg', 'image/png', 'image/webp'].some(type =>
          file.type.includes(type.replace('image/', ''))
      );

      if (!isImage) {
        Toast.fail('仅支持JPG/PNG/WEBP格式图片');
        return false;
      }

      try {
        // 统一压缩处理（无论大小）
        const compressedFile = await this.compressImage(file);

        // 显示压缩效果
        const ratio = (1 - compressedFile.size / file.size) * 100;
        console.log(`压缩率: ${ratio.toFixed(1)}%`);
        return compressedFile;
      } catch (error) {
        Toast.fail(`压缩失败: ${error.message}`);
        return false;
      }
    },
    // 改进后的压缩方法
    compressImage(file) {
      return new Promise((resolve, reject) => {
        new compressor(file, {
          quality: 0.6,
          maxWidth: 1920,
          maxHeight: 1080,
          convertSize: 1024 * 1024, // 超过1MB的PNG转JPEG
          success(result) {
            if (result.size > 1024 * 1024) {
              reject(new Error('无法压缩到1MB以下'));
            } else {
              resolve(result);
            }
          },
          error(err) {
            reject(new Error('压缩引擎错误'));
          }
        });
      });
    },
    // 新增方法 - 触摸开始（长按处理）
    handleTouchStart(index) {
      this.currentIndex = index;
      this.longPressTimer = setTimeout(() => {
        this.handleDelete(index);
      }, 1000); // 1秒长按触发
    },
    // 新增方法 - 触摸结束
    handleTouchEnd() {
      clearTimeout(this.longPressTimer);
    },
    // 新增方法 - 选择图片
    handleSelect(index) {
      this.previewList[index].selected = !this.previewList[index].selected;
      this.$forceUpdate();
      //返回选中
      const selectedList =  this.previewList.filter(item => item.selected);
      this.$emit('select-success', selectedList);
    },
    // 新增方法 - 一键清除
    handleClearAll() {
      Dialog.confirm({
        title: '清除确认',
        message: '确定要清除所有图片吗？',
      }).then(() => {
        this.previewList = [];
        this.$http.delete(`/api/dlrUserMaterial/clear`).then(() => {
          Toast.success('已清除所有图片');
        }).catch(() => {
          Toast.fail('清除失败！');
        })
      }).catch(()=>{
        console.log('取消清除！');
      });
    },
    loadPicList() {
      Toast.loading('加载中...');
      this.$http.get('/api/dlrUserMaterial/queryPageList', {
        params: {
          page: this.page,
          size: this.size
        }
      }).then(res => {
        this.previewList = res.data.content;
      }).finally(() => {
        Toast.clear(true);
      });
    },
    // 处理文件上传
    async handleUpload(files) {
      try {
        // 校验上传总数
        const currentCount = this.previewList.length;
        // 统一转换为数组
        const uploadFiles = Array.isArray(files) ? files : [files];

        // 计算剩余可上传数量
        const remaining = this.maxCount - currentCount;

        if (remaining <= 0) {
          Toast.fail({
            message: `已达最大上传数量（${this.maxCount}张）`,
            duration: 3000
          });
          this.fileList = [];
          return;
        }

        if (uploadFiles.length > remaining) {
          Toast.fail({
            message: `最多还可上传${remaining}张（当前选择${uploadFiles.length}张）`,
            duration: 3000
          });
          this.fileList = [];
          return;
        }

        // 创建上传任务队列
        const tasks = uploadFiles.map(file =>
            this.uploadSingleFile(file)
                .finally(() => this.removeFromFileList(file))
        );

        // 等待所有任务完成
        const results = await Promise.allSettled(tasks);

        // 统计成功数量
        const successCount = results.filter(r => r.status === 'fulfilled').length;

        // 显示结果提示
        if (successCount === tasks.length) {
          Toast.success(`全部上传成功（共 ${successCount} 张）`);
        } else {
          Toast.fail(`成功上传 ${successCount} 张，失败 ${tasks.length - successCount} 张，包含敏感信息！`);
        }
      } catch (error) {
        Toast.fail('上传过程发生错误');
      }
    },
    // 新增方法：从 fileList 移除文件
    removeFromFileList(targetFile) {
      const index = this.fileList.findIndex(file => file === targetFile);
      if (index > -1) {
        this.fileList.splice(index, 1);
      }
    },
    // 单个文件上传逻辑
    async uploadSingleFile(file) {
      const fileDir = 'material/person/' + this.userInfo.id;
      const formData = new FormData();

      formData.append('file', file.file); // 注意这里使用 file.file
      formData.append('dir', fileDir);

      const toast = Toast.loading({
        duration: 0, // 持续展示 toast
        forbidClick: true,
        message: "上传中...",
      });
      try {
        const res = await this.$http.post('/api/upload/uploadInnerFile', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.clear();
        this.previewList.push({ resUrl: res.data });
        this.$emit('upload-success', res.data);
        return true;
      } catch (err) {
        toast.clear();
        this.$emit('upload-error', err);
        throw err;
      }
    },
    // 删除图片
    handleDelete(index) {
      Dialog.confirm({
        title: '删除确认',
        message: '确定要删除此图片吗？',
      }).then(() => {
        const id = this.previewList[index].id;
        this.$http.delete(`/api/dlrUserMaterial/delete?id=${id}`).then(()=>{
          this.previewList.splice(index, 1);
        })
      });
    },

    // 文件过大提示
    onOversize() {
      Toast.fail(`文件大小不能超过 ${this.maxSize / 1024}MB`);
    }
  }
};
</script>

<style scoped>
/* 新增样式 */
.action-bar {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  background-color: #FFF;
  padding: 10px 20px; /* 调整 padding */
  border-radius: 10px;
  min-height: 60px; /* 设置最小高度，确保足够容纳按钮 */
}

/* 选中标记样式 */
.checkmark {
  position: absolute;
  top: 4px;
  right: 4px;
  background: #07c160;
  border-radius: 50%;
  padding: 2px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.checkmark .van-icon {
  color: white;
  display: block;
}

/* 长按效果 */
.preview-item:active {
  opacity: 0.7;
  transform: scale(0.98);
}

/* 调整原有样式 */
.preview-item {
  transition: all 0.2s ease; /* 简化过渡效果 */
}
.photo-picker {
  padding: 16px;
}

.van-uploader {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

/* 上传按钮美化 */
/*.van-uploader :deep(.van-button) {*/
/*  background: linear-gradient(135deg, #4a90e2, #63a4ff);*/
/*  border: none;*/
/*  border-radius: 24px;*/
/*  box-shadow: 0 4px 12px rgba(74, 144, 226, 0.25);*/
/*  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);*/
/*}*/

/*.van-uploader :deep(.van-button):hover {*/
/*  transform: translateY(-2px);*/
/*  box-shadow: 0 6px 16px rgba(74, 144, 226, 0.4);*/
/*}*/

/* 预览网格布局优化 */
.preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
  gap: 6px;
  margin-top: 10px;
}

.preview-item {
  position: relative;
  width: 100%;
  padding-top: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 3px solid #FFF;
}

.preview-item:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

/* 图片样式优化 */
.van-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.3s ease;
}

.van-image:hover {
  transform: scale(1.05);
}

/* 删除按钮美化 */
.delete-icon {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 18px;
  color: #fff !important;
  background: rgba(255, 80, 80, 0.9);
  border-radius: 50%;
  padding: 4px;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.delete-icon:hover {
  background: #ff5050;
  transform: scale(1.1);
}

/* 响应式布局 */
@media (max-width: 480px) {
  .preview-grid {
    gap: 8px;
  }

  .preview-item {
    border-radius: 8px;
  }
}
</style>
